import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Separator } from '@/components/ui/separator'
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CreditCard,
  RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import { useQueryClient } from '@tanstack/react-query'
import useGetBillingSubscription, {
  billingSubscriptionQueryKey,
} from '@/services/api/billing/useGetBillingSubscription'
import useSwitchTeam from '@/services/api/team/useSwitchTeam'
import {
  formatBillingDate,
  formatMoney,
  INTERVAL_LABEL,
} from '@/components/billing/format'
import SpaceSelectComponent from './SpaceSelectComponent'
import useAuthStore from '@/store/useAuthStore'

/**
 * Per-space billing summary. All actions live on /billing, which works on the
 * current space — so another space is switched to first.
 */
export default function BillingSection() {
  const navigate = useNavigate()
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const [selectedTeamId, setSelectedTeamId] = useState<string>(currentTeamId)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const queryClient = useQueryClient()
  const { action: switchTeam, status: switchStatus } = useSwitchTeam()
  const isSwitching = switchStatus.isPending

  const {
    data: billing,
    isLoading,
    status,
    refetchForce,
  } = useGetBillingSubscription({ teamId: selectedTeamId })

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      queryClient.removeQueries({
        queryKey: billingSubscriptionQueryKey(selectedTeamId),
      })
      await refetchForce()
    } catch {
      toast.error('Failed to refresh subscription data')
    } finally {
      setIsRefreshing(false)
    }
  }

  const openBilling = async () => {
    if (String(selectedTeamId) !== String(currentTeamId)) {
      try {
        await switchTeam({ teamId: Number(selectedTeamId) })
      } catch {
        toast.error('Could not switch space')
        return
      }
    }
    navigate('/billing')
  }

  const sub = billing?.subscription
  const cancelling = !!sub?.cancel_at

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2>Billing</h2>
          <p className="text-muted-foreground">
            Subscription and billing for each of your spaces
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing || isLoading || !selectedTeamId}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      <Separator />

      <div className="space-y-4">
        <SpaceSelectComponent
          selectedTeamId={selectedTeamId}
          onValueChange={(val) => setSelectedTeamId(val)}
        />

        {status === 'error' && (
          <Card className="w-full p-6">
            <div className="flex items-center space-x-2">
              <ExclamationTriangleIcon className="h-4 w-4 text-destructive" />
              <span>Failed to load subscription details…</span>
            </div>
          </Card>
        )}

        {selectedTeamId && status !== 'error' && (
          <Card className="w-full p-6">
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <span>Loading subscription details…</span>
              </div>
            ) : (
              <>
                {billing?.has_active_subscription && sub ? (
                  <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Plan</p>
                        <p>
                          {sub.plan_name}
                          {sub.amount != null &&
                            sub.currency &&
                            sub.interval && (
                              <span className="text-sm text-muted-foreground">
                                {' '}
                                · {formatMoney(sub.amount, sub.currency)} /{' '}
                                {INTERVAL_LABEL[sub.interval]}
                              </span>
                            )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {cancelling
                            ? 'Access ends'
                            : sub.is_trialing
                              ? 'Trial ends'
                              : 'Next payment'}
                        </p>
                        <p>
                          {formatBillingDate(
                            cancelling
                              ? sub.cancel_at
                              : sub.is_trialing
                                ? sub.trial_ends_at
                                : sub.next_billing_date,
                          )}
                        </p>
                      </div>
                    </div>
                    {sub.payment_issue && (
                      <p className="flex items-center gap-2 text-sm text-destructive md:col-span-2">
                        <AlertTriangle className="size-4" />A payment failed —
                        update the payment details to keep access.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="mb-4">
                    <h3 className="mb-1">No active subscription</h3>
                    <p className="text-sm text-muted-foreground">
                      This space doesn&apos;t have an active subscription.
                    </p>
                  </div>
                )}

                {billing?.is_billing_owner ? (
                  <Button
                    onClick={openBilling}
                    disabled={isSwitching}
                    className="w-full sm:w-auto"
                  >
                    {billing?.has_active_subscription
                      ? 'Manage billing'
                      : 'Choose a plan'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                    Only the space owner can manage billing for this space.
                  </p>
                )}
              </>
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
